import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as SubscriptionsActions from './subscriptions.actions';
import { ISubscription } from '@care-portals/api-interfaces';
/**
 * Interface for the 'Subscription' data
 */
type SubscriptionsEntity = ISubscription;

export const SUBSCRIPTIONS_FEATURE_KEY = 'subscriptions';

export interface State extends EntityState<SubscriptionsEntity> {
  selectedId?: string | number;
  loading: boolean;
  hasMore: boolean;
  fetched?: Date;
  error?: string | null;
  discountModal: boolean;
  filters?: {
    limit: number;
    offset: number;
    sortKey: string;
    sortOrder: string;
  };
}

export interface SubscriptionsPartialState {
  readonly [SUBSCRIPTIONS_FEATURE_KEY]: State;
}

export function selectActiveId(subscription: SubscriptionsEntity) {
  return subscription._id;
}

export const subscriptionsAdapter: EntityAdapter<SubscriptionsEntity> =
  createEntityAdapter<SubscriptionsEntity>({
    selectId: selectActiveId,
    sortComparer: false
  });

export const initialState: State = subscriptionsAdapter.getInitialState({
  // set initial required properties
  loading: false,
  error: null,
  discountModal: false,
  hasMore: false,
  filters: {
    limit: null,
    offset: null,
    sortKey: null,
    sortOrder: null
  }
});

const subscriptionsReducer = createReducer(
  initialState,
  on(SubscriptionsActions.clearSubscription, state => ({
    ...state,
    selectedId: null
  })),
  on(SubscriptionsActions.resetSubscriptions, state =>
    subscriptionsAdapter.removeAll({
      ...state,
      error: null
    })
  ),
  // get subscriptions
  on(SubscriptionsActions.getSubscriptions, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.getSubscriptionsSuccess,
    (state, { subscriptionResp, loadType, filters }) => {
      if (loadType === 'older') {
        const currentSubscriptions = Object.values(state.entities);
        const newSubscriptions = [
          ...currentSubscriptions,
          ...subscriptionResp.data
        ];
        return subscriptionsAdapter.upsertMany(newSubscriptions, {
          ...state,
          loading: false,
          fetched: new Date(),
          hasMore: subscriptionResp.hasMore,
          filters
        });
      }
      return subscriptionsAdapter.setAll(subscriptionResp.data, {
        ...state,
        loading: false,
        fetched: new Date(),
        hasMore: subscriptionResp.hasMore,
        filters
      });
    }
  ),
  on(SubscriptionsActions.getSubscriptionsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(SubscriptionsActions.getSubscription, state => ({
    ...state,
    loading: true,
    error: null
  })),

  on(SubscriptionsActions.getSubscriptionSuccess, (state, { subscription }) =>
    subscriptionsAdapter.upsertOne(subscription, {
      ...state,
      loading: false,
      selectedId: subscription._id
    })
  ),

  on(SubscriptionsActions.getSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // cancel subscriptions
  on(SubscriptionsActions.cancelSubscription, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.cancelSubscriptionSuccess,
    (state, { subscription }) => {
      return subscriptionsAdapter.updateOne(
        { id: subscription._id, changes: { status: 'cancelled' } },
        {
          ...state,
          loading: false
        }
      );
    }
  ),
  on(SubscriptionsActions.cancelSubscriptionFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  //update subscriptions
  on(SubscriptionsActions.updateSubscription, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.updateSubscriptionSuccess,
    (state, { subscription }) =>
      subscriptionsAdapter.upsertOne(subscription, {
        ...state,
        loading: false
      })
  ),
  on(SubscriptionsActions.updateSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // apply discount
  on(SubscriptionsActions.applyDiscountForInvoice, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.applyDiscountForInvoiceSuccess,
    (state, { discount }) => ({
      ...state,
      loading: false,
      error: null
    })
  ),
  on(
    SubscriptionsActions.applyDiscountForInvoiceFailure,
    (state, { error }) => ({
      ...state,
      loading: false,
      error
    })
  ),
  on(SubscriptionsActions.openDiscountModal, (state, { subscription }) => ({
    ...state,
    discountModal: true,
    selectedId: subscription._id
  })),
  on(SubscriptionsActions.closeDiscountModal, state => ({
    ...state,
    discountModal: false
  })),

  on(SubscriptionsActions.createSubscription, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.createSubscriptionSuccess,
    (state, { subscription }) =>
      subscriptionsAdapter.upsertOne(subscription, {
        ...state,
        loading: false
      })
  ),
  on(SubscriptionsActions.createSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return subscriptionsReducer(state, action);
}
